import momentTimezone from 'moment-timezone';
import {formatCurrency, translate} from 'views/module';

const DAY_LABEL = {
  MO: translate('timeFormat.DaysOfTheWeek.Monday'),
  TU: translate('timeFormat.DaysOfTheWeek.Tuesday'),
  WE: translate('timeFormat.DaysOfTheWeek.Wednesday'),
  TH: translate('timeFormat.DaysOfTheWeek.Thursday'),
  FR: translate('timeFormat.DaysOfTheWeek.Friday'),
  SA: translate('timeFormat.DaysOfTheWeek.Saturday'),
  SU: translate('timeFormat.DaysOfTheWeek.Sunday'),
};

const FLEXIBLE_DAY_LABEL = {
  ANY_DAY: translate('jobDetail.RequiredDays.AnyDay'),
  Sun: translate('jobDetail.RequiredDays.Sun'),
  Mon: translate('jobDetail.RequiredDays.Mon'),
  Tue: translate('jobDetail.RequiredDays.Tue'),
  Wed: translate('jobDetail.RequiredDays.Wed'),
  Thu: translate('jobDetail.RequiredDays.Thu'),
  Fri: translate('jobDetail.RequiredDays.Fri'),
  Sat: translate('jobDetail.RequiredDays.Sat'),
};

const FLEXIBLE_TIME_LABEL = {
  ANY_TIME: translate('jobDetail.RequiredDays.AnyTime'),
  MORNING: translate('jobDetail.RequiredDays.Morning'),
  AFTERNOON: translate('jobDetail.RequiredDays.Afternoon'),
  EVENING: translate('jobDetail.RequiredDays.Evening'),
  OVERNIGHT: translate('jobDetail.RequiredDays.Overnight'),
};

const parseDurationHours = (rawValue) => {
  const durationText = `${rawValue ?? ''}`.trim();
  if (!durationText) {
    return 0;
  }

  const parsedHours = Number.parseFloat(durationText.replace(/[^0-9.]/g, ''));
  return Number.isFinite(parsedHours) ? parsedHours : 0;
};

const formatLabelList = (values = [], labelMap = {}) => {
  return values
      .map((value) => labelMap[value] || value)
      .join(', ');
};

const formatJobScheduleStrings = (schedule) => {
  if (!schedule?.type) {
    return [];
  }

  if (schedule.type === 'RECURRENCE_EVENT') {
    const timezone = schedule?.recurrenceRule?.timezone || 'UTC';
    return (schedule?.recurrenceRule?.weeklySlots || []).map((slot) => {
      const startTime = momentTimezone(slot.startTime).tz(timezone);
      const endTime = momentTimezone(slot.endTime).tz(timezone);
      const dayText = DAY_LABEL[slot.day] ?? slot.day;
      const startFormatted = startTime.format(translate('timeFormat.Time'));
      const endFormatted = endTime.format(translate('timeFormat.Time'));
      return translate('timeFormat.TimeFrame', {
        dayText,
        startFormatted,
        endFormatted,
      });
    });
  }

  if (schedule.type === 'LIVEIN_EVENT') {
    return [translate('jobDetail.RequiredDays.LiveIn')];
  }

  if (schedule.type === 'FLEXIBLE') {
    const lines = [translate('jobDetail.RequiredDays.Flexible')];
    const flexibleSchedule = schedule.flexibleSchedule || {};
    const preferredDays = formatLabelList(flexibleSchedule.preferredDays, FLEXIBLE_DAY_LABEL);
    const preferredTimes = formatLabelList(flexibleSchedule.preferredTimes, FLEXIBLE_TIME_LABEL);

    if (preferredDays) {
      lines.push(translate('jobDetail.RequiredDays.PreferredDays', {value: preferredDays}));
    }

    if (preferredTimes) {
      lines.push(translate('jobDetail.RequiredDays.PreferredTimes', {value: preferredTimes}));
    }

    if ((flexibleSchedule.visitDuration || '').trim()) {
      lines.push(translate('jobDetail.RequiredDays.VisitDuration', {value: flexibleSchedule.visitDuration.trim()}));
    }

    return lines;
  }

  return [];
};

const parseJobData = (job) => {
  const currency = 'CAD';
  const hourlyRate = job.price.hourlyRate;
  const formatedHourlyRate = formatCurrency(hourlyRate, currency, 'en-CA');
  const payPerHour = translate('jobBoard.SalaryDetails.PayPerHour', {
    currency: currency,
    formatedHourlyRate: formatedHourlyRate,
  });
  const recurrenceType = job.schedule.type;
  const scheduleStringsFormated = formatJobScheduleStrings(job.schedule);
  let hoursPerWeek = 0;
  let weeklyRate = 0;
  let numShifts = 0;
  let hoursPerWeekString = '';
  let shifts = '';
  let salaryInfo = payPerHour;
  let summaryLabel = '';
  switch (recurrenceType) {
    case 'RECURRENCE_EVENT':
      const weeklySlots = job.schedule.recurrenceRule.weeklySlots;
      // Calculate salary info to display
      hoursPerWeek = weeklySlots.reduce((totalHours, slot) => {
        const timezone = job.schedule.recurrenceRule.timezone || 'UTC';
        const startTime = momentTimezone(slot.startTime).tz(timezone);
        const endTime = momentTimezone(slot.endTime).tz(timezone);
        const duration = momentTimezone.duration(endTime.diff(startTime));
        return totalHours + duration.asHours();
      }, 0);
      hoursPerWeek = Math.floor(hoursPerWeek);
      weeklyRate = hoursPerWeek*hourlyRate;
      numShifts = weeklySlots.length;
      hoursPerWeekString = translate('jobBoard.SalaryDetails.HoursPerWeek', {hoursPerWeek: hoursPerWeek});
      shifts = translate('jobBoard.SalaryDetails.Shifts', {numShifts: numShifts});
      salaryInfo = translate('jobBoard.SalaryDetails.SalaryInfo', {
        payPerHour: payPerHour,
        hoursPerWeekString: hoursPerWeekString,
        shifts: shifts,
      });
      summaryLabel = translate('jobDetail.PayAndHours.Shifts', {numShifts});
      break;
    case 'LIVEIN_EVENT':
      const hours = 8;
      const days = 7;
      hoursPerWeek = hours*days;
      weeklyRate = hoursPerWeek*hourlyRate;
      numShifts = 7;
      hoursPerWeekString = translate('jobBoard.SalaryDetails.HoursPerWeek', {hoursPerWeek: hoursPerWeek});
      summaryLabel = translate('jobDetail.PayAndHours.Shifts', {numShifts});
      break;
    case 'FLEXIBLE':
      const flexibleSchedule = job.schedule.flexibleSchedule || {};
      numShifts = flexibleSchedule.preferredDays?.length || 0;
      hoursPerWeek = parseDurationHours(flexibleSchedule.visitDuration) * numShifts;
      if (hoursPerWeek > 0 && numShifts > 0) {
        weeklyRate = hoursPerWeek * hourlyRate;
        hoursPerWeekString = translate('jobBoard.SalaryDetails.HoursPerWeek', {hoursPerWeek});
        shifts = translate('jobBoard.SalaryDetails.Shifts', {numShifts});
        salaryInfo = translate('jobBoard.SalaryDetails.SalaryInfo', {
          payPerHour,
          hoursPerWeekString,
          shifts,
        });
        summaryLabel = translate('jobDetail.PayAndHours.Shifts', {numShifts});
      } else {
        salaryInfo = translate('jobBoard.SalaryDetails.FlexibleInfo', {payPerHour});
        summaryLabel = translate('jobDetail.PayAndHours.Flexible');
      }
      break;
    default:
      break;
  }
  const payPerWeekString = weeklyRate > 0 ?
    translate('jobBoard.SalaryDetails.PayPerWeek', {
      currency: currency,
      formatedWeeklyRate: formatCurrency(weeklyRate, currency, 'en-CA'),
    }) :
    translate('jobBoard.SalaryDetails.PayVaries');
  return {
    payPerHour: payPerHour,
    scheduleStringsFormated: scheduleStringsFormated,
    hoursPerWeekString: hoursPerWeekString,
    numShifts: numShifts,
    shifts: shifts,
    salaryInfo: salaryInfo,
    payPerWeekString: payPerWeekString,
    summaryLabel: summaryLabel,
    recurrenceType: recurrenceType,
  };
};

export {
  formatJobScheduleStrings,
  parseJobData,
};
